import { UnderwritingPolicyAttachmentStatus } from "../../models/underwritingPolicyAttachment";

export interface ActorLogEntry {
  status: UnderwritingPolicyAttachmentStatus;
  actorEmailId?: string;
  actorId?: string;
  createdAt?: string;
  timestamp?: string;
}

export interface AttachmentActorInfo {
  createdBy?: string;
  createdAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export function getEventActor(entry: {
  actorEmailId?: string;
  actorId?: string;
}): string {
  return entry.actorEmailId || entry.actorId || "-";
}

function getEntryTime(entry: ActorLogEntry): string | undefined {
  return entry.createdAt || entry.timestamp;
}

export function getActorRoleLabel(status: UnderwritingPolicyAttachmentStatus): string {
  if (status === UnderwritingPolicyAttachmentStatus.SHADOW) return "Requested By";
  if (
    status === UnderwritingPolicyAttachmentStatus.ACTIVE ||
    status === UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT
  )
    return "Approved By";
  if (status === UnderwritingPolicyAttachmentStatus.INACTIVE) return "Deactivated By";
  return "Actor";
}

export function computeActorInfo(logs: ActorLogEntry[]): AttachmentActorInfo {
  const sorted = [...logs].sort((a, b) => {
    return new Date(getEntryTime(a) || 0).getTime() - new Date(getEntryTime(b) || 0).getTime();
  });

  const shadow = sorted.find((entry) => entry.status === UnderwritingPolicyAttachmentStatus.SHADOW);
  const activeEntries = sorted.filter((entry) => entry.status === UnderwritingPolicyAttachmentStatus.ACTIVE);
  const experimentEntries = sorted.filter((entry) => entry.status === UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT);

  const latestByTime = (entries: ActorLogEntry[]) =>
    [...entries].sort((a, b) => new Date(getEntryTime(b) || 0).getTime() - new Date(getEntryTime(a) || 0).getTime())[0];

  const approved = activeEntries.length > 0 ? latestByTime(activeEntries) : experimentEntries.length > 0 ? latestByTime(experimentEntries) : undefined;

  return {
    createdBy: shadow ? getEventActor(shadow) : undefined,
    createdAt: shadow ? getEntryTime(shadow) : undefined,
    approvedBy: approved ? getEventActor(approved) : undefined,
    approvedAt: approved ? getEntryTime(approved) : undefined,
  };
}

export function deriveAttachmentActorInfoMap<
  T extends ActorLogEntry & { attachmentId: string }
>(events: T[]): Map<string, AttachmentActorInfo> {
  const grouped = new Map<string, T[]>();

  for (const event of events) {
    const current = grouped.get(event.attachmentId) || [];
    current.push(event);
    grouped.set(event.attachmentId, current);
  }

  const result = new Map<string, AttachmentActorInfo>();
  for (const [attachmentId, items] of Array.from(grouped.entries())) {
    result.set(attachmentId, computeActorInfo(items));
  }
  return result;
}

export function findNearestPrecedingShadow<
  T extends ActorLogEntry & { attachmentId: string }
>(event: T, sameAttachmentEventsAscending: T[]): T | undefined {
  const eventTime = new Date(getEntryTime(event) || 0).getTime();
  const shadows = sameAttachmentEventsAscending.filter(
    (entry) =>
      entry.status === UnderwritingPolicyAttachmentStatus.SHADOW &&
      new Date(getEntryTime(entry) || 0).getTime() <= eventTime
  );
  return shadows.sort((a, b) => new Date(getEntryTime(b) || 0).getTime() - new Date(getEntryTime(a) || 0).getTime())[0];
}
